import { useLocalObservable } from 'mobx-react-lite';
import { ElementType } from 'react';

import { TLoginLayoutMessage } from '@gem/components';
import { useTranslation } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { IDiscoverDestination } from 'APP/model/tenantRouter/tenantRouterModel.types';
import { ProjectUrls } from 'APP/packages/projectUrls/ProjectUrls';
import { storage } from 'APP/packages/storage';
import { ProjectInstallationType } from 'CONFIG_TYPES';
import Entities from 'STORE';

import { LOGIN_ERROR_MESSAGE_KEYS, LoginError } from './Login.constants';
import { ILoginPresenter, ILoginProps, ILoginViewProps } from './Login.types';
import { LoginAddNewServerStep } from './LoginViews/LoginAddNewServerStep/LoginAddNewServerStep';
import { LoginDestinationTypeStep } from './LoginViews/LoginDestinationTypeStep/LoginDestinationTypeStep';
import { LoginEmailStep } from './LoginViews/LoginEmailStep/LoginEmailStep';
import { LoginRegionSelectStep } from './LoginViews/LoginRegionSelectStep/LoginRegionSelectStep';
import { LoginRouterErrorView } from './LoginViews/LoginRouterErrorView/LoginRouterErrorView';
import { LoginServerSelectStep } from './LoginViews/LoginServerSelectStep/LoginServerSelectStep';
import { LoginWithAuthorizationConfig } from './LoginViews/LoginWithAuthorizationConfig/LoginWithAuthorizationConfig';

const InitialLoginView = storage.authorizationConfig.get()
  ? LoginWithAuthorizationConfig
  : LoginEmailStep;

export function useLoginPresenter(params: ILoginProps): ILoginPresenter {
  const { t } = useTranslation();
  const presenter = useLocalObservable<ILoginPresenter>(() => ({
    email: '',
    isRouterError: false,
    isShowOtherOptions: !!params.isShowOtherOptions,
    loginViewsStack: [InitialLoginView],
    destinationKey: '',

    get discoverDestinations(): IDiscoverDestination[] {
      return Entities.authStore.discoverDestinations;
    },

    get serversDestinations(): IDiscoverDestination[] {
      return Entities.authStore.serversDestinations;
    },

    get regionsDestinations(): IDiscoverDestination[] {
      return Entities.authStore.regionsDestinations;
    },

    get destination(): IDiscoverDestination | null {
      return Entities.authStore.getDiscoverDestinationByKey(presenter.destinationKey);
    },

    get loginCurrentView(): ElementType<ILoginViewProps> | null {
      return presenter.loginViewsStack[presenter.loginViewsStack.length - 1];
    },

    get isOffline(): boolean {
      return Entities.App.isOfflineNetworkStatus;
    },

    get onBackHandler(): (() => void) | null {
      if (presenter.loginViewsStack.length === 1) {
        return params.onBack || null;
      }
      return () => {
        presenter.resetErrors();
        presenter.loginViewsStack.pop();
      };
    },

    get error(): LoginError | null {
      if (presenter.isOffline) {
        return LoginError.Offline;
      }
      if (presenter.isRouterError) {
        return LoginError.NoOrganization;
      }

      return null;
    },

    get errorMessage(): TLoginLayoutMessage | null {
      if (!presenter.error) {
        return null;
      }
      return {
        type: 'error',
        content: t(LOGIN_ERROR_MESSAGE_KEYS[presenter.error]),
      };
    },

    get isBaseUrlConfigured(): boolean {
      return ProjectUrls.isCorrectConfig;
    },

    setEmail(email: string): void {
      presenter.email = email;
    },

    setIsShowOtherOptions(isShowOtherOptions: boolean): void {
      presenter.isShowOtherOptions = isShowOtherOptions;
    },

    setDestinationKey(key: string): void {
      presenter.destinationKey = key;
    },

    openView(View: ElementType<ILoginViewProps>): void {
      presenter.loginViewsStack.push(View);
    },

    openDestinationsView(type: ProjectInstallationType): void {
      if (type === ProjectInstallationType.AllInOne) {
        presenter.openView(LoginDestinationTypeStep);
      }

      if (type === ProjectInstallationType.CloudOnly) {
        presenter.openRegionsDestinationsView();
      }

      if (type === ProjectInstallationType.OnPremiseOnly) {
        presenter.openServersDestinationsView();
      }
    },

    openRegionsDestinationsView(): void {
      if (!presenter.regionsDestinations.length) {
        return presenter.openView(LoginRouterErrorView);
      }

      if (presenter.regionsDestinations.length === 1) {
        presenter.onAuthorizeWithNewConfig(presenter.discoverDestinations[0].key);
        return;
      }

      presenter.openView(LoginRegionSelectStep);
    },

    openServersDestinationsView(): void {
      if (!presenter.serversDestinations.length) {
        return presenter.openView(LoginAddNewServerStep);
      }
      presenter.openView(LoginServerSelectStep);
    },

    resetErrors(): void {
      presenter.isRouterError = false;
    },

    async onSubmitEmail(email: string): Promise<void> {
      presenter.isRouterError = false;

      const destinationType = PROJECT_CONFIG.installationType;
      presenter.email = email;
      Entities.authStore.setDiscoverDestinations(storage.discoverDestinations.get(presenter.email));

      await Tasks.tenantRouter.getDiscoverDestinations({
        email: presenter.email,
        type: destinationType,
      });

      Entities.authStore.preferredDiscoverDestinationKey =
        storage.preferredDiscoverDestinationKey.get(email);

      presenter.openDestinationsView(destinationType);
    },

    async updateBaseUrl(): Promise<boolean> {
      presenter.isRouterError = false;

      if (!presenter.destination) {
        presenter.handleRouterError();
        return false;
      }

      if (presenter.destination.baseUrl) {
        storage.tenantRouting.update({ bffUrl: presenter.destination.baseUrl });
        return true;
      }

      const isBaseUrl = await Tasks.tenantRouter.updateDestinationBaseUrl(presenter.destination);
      if (!isBaseUrl) {
        presenter.handleRouterError();
        return false;
      }

      return true;
    },

    async onAuthorizeWithNewConfig(destinationKey: string): Promise<boolean> {
      presenter.setDestinationKey(destinationKey);
      if (!presenter.isBaseUrlConfigured) {
        await presenter.updateBaseUrl();
      }

      if (presenter.isRouterError || !presenter.destination) {
        return false;
      }

      const isSuccess = await presenter.onAuthorize();

      if (isSuccess) {
        storage.authorizationConfig.set({
          email: presenter.email,
          baseUrl: storage.tenantRouting.get()?.bffUrl || '',
          alias: presenter.destination.alias,
          type: presenter.destination.type,
        });

        storage.preferredDiscoverDestinationKey.set(presenter.email, presenter.destination.key);
      }
      return isSuccess;
    },

    async onAuthorize(): Promise<boolean> {
      presenter.isRouterError = false;

      const { isSuccess: canRedirect } = await Tasks.authorization.redirectToADAuth(
        presenter.email
      );

      if (!canRedirect) {
        presenter.handleRouterError();
        return false;
      }

      return true;
    },

    handleRouterError(): void {
      storage.tenantRouting.remove();
      presenter.isRouterError = true;
    },
  }));

  return presenter;
}
